import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import type { RootState } from '../../../store/store';
import { isActiveService } from '../../../util/service-catalog';
import {
  setImageAssets,
  resetImageAssets,
  clearPmsDirtyFlags
} from '../slice/ImageAssetSlice';
import {
  assetApi,
  useLazyGetAssetsByOrderQuery,
  useLazyGetPhotosetsQuery,
  useLazyGetPropertyByIdQuery
} from '../services/assetApi';
import {
  AssetManagement,
  type ImageAssetType
} from '../model/AssetsManagement';
import type { Asset, AssetSelection } from '../../../types/asset.types';
import { toImageAsset } from '../model/toImageAsset';
import { matchesAssetQuery } from '../property-asset-view/asset-images/assetSearch';
import { isInQueryScope } from '../model/assetViewScope';
import { isPhotoAsset } from '../model/isPhotoAsset';
import type { PhotosetOrderStage } from '../model/orderStage';
import {
  downloadAllSelection,
  hiddenDownloadPool,
  type DownloadScope
} from '../download-assets/downloadScope';
import { isInternalAssetTeamUser } from '../assetFeatureFlags';
import { useIntegrationStatus } from '../../pms/hooks/useIntegrationStatus';
import { useSessionPmsAccount } from '../../pms/hooks/useSessionPmsAccount';
import { vendorDisplayName } from '../../pms/vendor-settings/vendorDisplayName';
import {
  useGetAssetServicesQuery,
  useGetOrderByUUIDQuery
} from '../../orders/services/orderApi';
import { canCreateOnRoute } from '../../../util/permissions';
import { toStreetAndLocality } from '../../../util/formatting';
import { getPropertyAddress } from '../../../services/property-address';
import type { ServiceType } from '@/components/ui/carousel';
import { useNavigationService } from '../../../services/navigate-service';
import { ROUTES, buildRoute } from '../../../config/routes';
import { orderViewQuery } from '../../orders/order-views/orderViews';
import { getOrgSelectedAllData } from '../../../services/org-data';
import type { PropertyEntry } from '../../properties/property-shared/propertyEntry';
import { is4xxError } from '../../../services/api-base';
import { track } from '../../../services/telemetry';
import { workspacePmsAccount } from './useWorkspaceAccount';

dayjs.extend(localizedFormat);

// DV-3399: how long load() waits for an in-flight getPhotosets refetch to land
// before re-reading the list. 50 × 100 ms = 5 s at most.
const PHOTOSETS_SETTLE_POLL_MS = 100;
const PHOTOSETS_SETTLE_MAX_POLLS = 50;

/** Resolves once the getPhotosets(propertyId) entry is no longer pending. */
async function photosetsListSettled(
  getState: () => RootState,
  propertyId: string,
  isSuperseded: () => boolean
): Promise<void> {
  for (let i = 0; i < PHOTOSETS_SETTLE_MAX_POLLS; i += 1) {
    const entry = assetApi.endpoints.getPhotosets.select(propertyId)(
      getState()
    );
    if (entry.status !== 'pending' || isSuperseded()) return;
    await new Promise((resolve) =>
      setTimeout(resolve, PHOTOSETS_SETTLE_POLL_MS)
    );
  }
}

interface PropertyMeta {
  propertyAddress: string;
  /** Header display lines (Unit 4 spec §1.1, round 4 + round 5): street
   *  (address 1, address 2, unit — comma-joined) on the title line,
   *  "City, ST Zip" below it in muted text. BOTH sources — the router-state
   *  seed and the property fetch — derive these via `toStreetAndLocality`
   *  from `getPropertyAddress`'s output, the same rule the properties list
   *  renders, so the header text can't switch when the fetch replaces the
   *  seed. localityLine is null when the address has nothing to split. */
  streetLine: string;
  localityLine: string | null;
  propertyType: string;
  hasPhotoset: boolean;
  propertySource: string;
  orderUUID: string | null;
  /** Latest order's stage, straight from the property record (DV-2820). Drives
   *  the pre-delivery upload gate; null until the property fetch resolves. */
  orderStatus: string | null;
  // Raw fields (as opposed to the formatted propertyAddress) needed for the
  // Download Assets zip filename (buildZipNameBase). Only populated once the
  // property fetch resolves — absent from the router-state-seeded header.
  address1?: string;
  unitNumber?: string | null;
  /** The property's own account (DV-2849 PR 2): the name is what the internal
   *  workspace's header shows, and the UUID keys its account read
   *  (`useWorkspaceAccount`). Null from the router-state seed, filled by the
   *  property fetch. property-service keeps `entity` in step with account
   *  renames (`updateEntityNameInAllProperties`). */
  entity: string | null;
  entityUUID: string | null;
}

interface PhotosetData {
  photosetId: string;
  name: string;
  createdAt: string;
  lastWebhookTriggeredAt?: string | null;
  lastPmsSyncedAt: string | null;
  assets: Asset[];
  zillowUrls: Array<{ url: string }>;
  // DV-2221: full backend display order (all asset types) — the permutation
  // base for reorder persistence. Falls back to the response array order
  // when the backend predates the field (prod).
  assetUUIDs: string[];
  hasUnsyncedChanges: boolean;
  hasUnsyncedPmsChanges: boolean;
  // DV-2452: display-order authority (see mapAssets). Still OPTIONAL for type
  // honesty — an old backend could omit it — but the rollout bridge that used
  // to read `undefined` specially is gone (DV-2820); undefined now falls to
  // sortOrder like `false`.
  orderCustomized?: boolean;
  // DV-2861: DV-2860's client-visibility flag, passed through raw (null and
  // absent both read visible). Read it only via visibility/isVisibleToClient.
  visibleToClient?: boolean | null;
  /** Public PhotoPlan gallery share token; null while the link is off. */
  publicToken?: string | null;
  /** DV-3521: the order the set was created for; see Photoset.orderUUID. */
  orderUUID: string | null;
  /** DV-3521: which system created the set; see Photoset.origin. */
  origin: string | null;
}

/** Photoset.origin of a set Connect created (asset-service `Photosets.ORIGIN_CONNECT`). */
const PHOTOSET_ORIGIN_CONNECT = 'connect';

const mapAssets = (
  items: Asset[],
  photosetId?: string,
  orderCustomized?: boolean
): ImageAssetType[] => {
  // Display-order authority (DV-2452 backend contract): a photoset's
  // orderCustomized flag decides. true → the photo was explicitly re-sequenced,
  // so trust photoset.assetUUIDs order (the response array is already in it, and
  // the reorder endpoint also rewrites per-asset sortOrder to match). false →
  // never reordered, so the stored list order is not meaningful (it can be
  // legacy HashSet-arbitrary from parallel processing) and we sort by
  // sortOrder. This is the same rule the backend uses for download-ZIP filename
  // numbering, so the grid and the ZIP always agree.
  //
  // DV-2820 cleanup: the `?? canUseEditSuite()` transitional fallback is gone.
  // Keeping it past the ungate would have been actively wrong — canUseEditSuite()
  // is now unconditionally true, so an undefined flag would start trusting a list
  // order the backend says is not meaningful. Undefined now reads falsy →
  // sortOrder, the safe branch on any backend.
  //
  // orderCustomized is a primitive boolean in asset-service (DV-2452, PR #173),
  // so a service running that code always sends it; absent-in-Mongo deserializes
  // to false, never undefined. CAVEAT: #173 reached the prod-BOUND branch on
  // 2026-07-27 — that is branch state, not proof of what prod is running. If the
  // UI ever ships against an older (or rolled-back) asset-service, a reorder
  // persists but comes back without the flag, so the grid reverts to sortOrder on
  // refresh and stops matching the ZIP numbering. Verify the asset-service deploy
  // alongside any release that makes reorder newly reachable.
  //
  // Order-scoped (non-photoset) fetches have no list order at all — the
  // Boolean(photosetId) guard keeps them on sortOrder, their only authority.
  //
  // DO NOT "simplify" this to trust the stored order unconditionally. Unit 4
  // PR B set out to (spec §2.6) and stopped: asset-service `main` still
  // branches on orderCustomized in AssetsServiceImpl#orderedGalleryPhotoUUIDs,
  // whose javadoc names THIS function as the rule it mirrors, and
  // Photosets.java says consumers must prefer sortOrder until a set is
  // explicitly re-sequenced. #173 fixed the writers going forward; it did not
  // backfill. Dropping the branch would trust an arbitrary order on every
  // photoset delivered before 2026-07-23 and desync the grid from download-ZIP
  // numbering. See the 2026-08-21 Correction in the Unit 4 spec.
  const keepBackendPhotosetOrder = Boolean(photosetId) && Boolean(orderCustomized);
  const ordered = keepBackendPhotosetOrder
    ? items
    : items.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  return ordered.map((item) => toImageAsset(item, photosetId));
};

/** Where the workspace was entered from (DV-2849). `account` is every /home
 *  route (today's behavior, `source` decides Back). `global-orders` is the
 *  internal route under /internal: Back returns to Global Orders, the PMS
 *  status read names the property's account (DV-3345), and no ordering
 *  surface is offered. A prop, not a pathname sniff or a fourth `source` token.
 *
 *  Aliases `PropertyEntry` so the hook and the two property heroes cannot
 *  drift apart — the heroes are also rendered by property-detail, so the
 *  union is declared there. */
export type AssetsEntry = PropertyEntry;

/** Where Back leads on the account entry, keyed on how the user arrived.
 *  One answer for both the navigation and its tooltip, so they cannot drift.
 *  `view` is the orders preset the link in carried (DV-3455); only the orders
 *  target reads it, and `orderViewQuery` drops All Orders and unknown keys. */
const accountBackTarget = (
  source: string | null | undefined,
  view: string | null
) => {
  if (source === 'cart') return { route: ROUTES.ORDERS_CART, query: {}, tooltip: 'Back to Cart' };
  if (source === 'order') {
    return { route: ROUTES.ORDERS, query: orderViewQuery(view), tooltip: 'Back to Orders' };
  }
  return { route: ROUTES.PROPERTIES, query: {}, tooltip: 'Back to Properties' };
};

/**
 * Whether the PMS sync surfaces render: a vendor is connected for the
 * workspace's account and the property came from that PMS. DV-3345: no
 * longer entry-dependent. The Global Orders photos page reads the
 * PROPERTY's account, and DV-3334 makes the sync resolve the account from
 * the photos on the server.
 */
export function canShowPmsSync({
  pmsSource,
  propertySource
}: {
  pmsSource: string | null;
  propertySource: string | null | undefined;
}): boolean {
  return Boolean(pmsSource) && propertySource === 'Sync';
}

/**
 * The stage of the selected photoset's own order: `no-order` when the set
 * names none, else its read's stage once landed, or whether that read is
 * still out or has failed.
 */
const toPhotosetOrderStage = ({
  orderUUID,
  orderRead,
  orderReadFailed
}: {
  orderUUID: string | null;
  orderRead: { orderStatus?: string | null } | undefined;
  orderReadFailed: boolean;
}): PhotosetOrderStage => {
  if (!orderUUID) return { state: 'no-order' };
  if (orderRead) return { state: 'known', status: orderRead.orderStatus ?? null };
  return orderReadFailed ? { state: 'failed' } : { state: 'loading' };
};

interface UsePropertyAssetsOptions {
  /** Whether the Share control is offered here (released, and a role that
   *  may share). Its gate is the only reader of `photosetOrderStage`, so
   *  without it the photoset's own order is not read, and the stage stays
   *  `loading` for a set that names one. */
  shareOffered?: boolean;
}

export function usePropertyAssets(
  entry: AssetsEntry = 'account',
  { shareOffered = true }: UsePropertyAssetsOptions = {}
) {
  const isInternalEntry = entry === 'global-orders';
  const { state } = useLocation();
  const navigate = useNavigate();
  const { navigateTo } = useNavigationService();
  const { propertyUUID: propertyId, photosetUUID } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') || state?.navigateFrom;
  // DV-3521: the order this workspace was opened for, from `?orderUUID=`. Both
  // order-row entries put it there (AssetActionCell on Global Orders, the
  // orders table through orderRowAssetsNav), so a reload, a new tab or a
  // copied link keeps it, and the in-page photoset redirect and switcher
  // carry the query forward. The query only: router state is also sent by
  // the properties list, whose `orderUUID` is the property's latest order
  // and not an order anyone opened. The property fetch below replaces
  // propertyMeta.orderUUID with that latest order, so this is kept apart.
  const contextOrderUUID = searchParams.get('orderUUID') || null;
  // DV-3521: that order's own photoset link, when it has one.
  const contextOrderPhotosetUUID =
    searchParams.get('orderPhotosetUUID') || null;
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const imageAssets = useSelector(
    (s: RootState) => s.imageAsset?.imageAssets || []
  );
  const searchQuery = useSelector(
    (s: RootState) => s.imageAsset?.searchQuery ?? ''
  );
  const photoPool = useSelector((s: RootState) => s.imageAsset?.pool ?? 'all');

  const [getPropertyByIdTrigger] = useLazyGetPropertyByIdQuery();
  const [getPhotosetsTrigger] = useLazyGetPhotosetsQuery();
  const [getAssetsByOrderTrigger] = useLazyGetAssetsByOrderQuery();

  const [propertyMeta, setPropertyMeta] = useState<PropertyMeta | null>(() => {
    if (!state?.propertyAddress) return null;
    // The seed is getPropertyAddress()'s newline-joined output (the properties
    // list passes its formattedAddress); the fetch below derives its lines
    // from the same rule, so the header text can't switch when it resolves.
    const { streetLine, localityLine } = toStreetAndLocality(
      state.propertyAddress
    );
    return {
      // Canonical flat form (street, City ST Zip) — same construction as the
      // fetch path below, so consumers of the joined string (support-request
      // payloads, Order Service) see one shape rather than the seed's raw
      // newline-joined router state.
      propertyAddress: localityLine
        ? `${streetLine}, ${localityLine}`
        : streetLine,
      streetLine,
      localityLine,
      propertyType: state.propertyType,
      hasPhotoset: state.hasPhotoset,
      propertySource: state.propertySource,
      // The list row's order, so the no-photoset grid can load before the
      // property fetch lands. Only a paint seed: the opened order is
      // contextOrderUUID (the query), and the properties list sends its
      // property's latest order here.
      orderUUID: state.orderUUID || null,
      // Router-state seed carries no order stage; the property fetch fills
      // it in. Null reads as "not delivered", so the upload gate starts
      // closed and opens once the real value lands — never the reverse.
      orderStatus: null,
      entity: null,
      entityUUID: null
    };
  });
  // DV-3345: one status read, for the workspace's account, gives the vendor
  // and the paused state. It replaced the legacy vendors call that read the
  // session (spec D5).
  const sessionAccount = useSessionPmsAccount();
  const pmsAccount = workspacePmsAccount(entry, propertyMeta?.entityUUID, sessionAccount);
  // Re-read on window focus: an Internal Admin resumes a paused connection in
  // the new tab the paused notice opens (spec D9), then comes back here.
  const { status: integrationStatus, isPending: pmsStatusPending } = useIntegrationStatus(pmsAccount, {
    refetchOnFocus: true
  });
  const pmsSource = integrationStatus.kind === 'none' ? null : vendorDisplayName(integrationStatus.pmsSource);
  const isPaused = integrationStatus.kind === 'disabled';
  const [isNotFound, setIsNotFound] = useState(false);
  const [isLoadError, setIsLoadError] = useState(false);
  // Start true: asset data is pending from the first render. The load effect
  // below only flips this after propertyMeta is set, so defaulting to false
  // would let the page paint its content shell for one frame before the
  // spinner — a visible flash on navigation. Stays true until load() settles.
  const [loading, setLoading] = useState(true);
  const [zillow3d, setZillow3d] = useState<string | null>(null);
  // Order-only path (no photoset): when the latest synced photo was synced.
  // The line itself is derived below once the vendor is known (spec D5).
  const [orderLastSyncedAt, setOrderLastSyncedAt] = useState<string | null>(null);
  const [photosets, setPhotosets] = useState<PhotosetData[]>([]);
  const [selectedPhotosetId, setSelectedPhotosetId] = useState<string | null>(
    null
  );
  const [hasPhotoset, setHasPhotoset] = useState(false);
  const [selectedImages, setSelectedImages] = useState<ImageAssetType[]>([]);
  // DV-2849 PR 3 (spec §4.5): on the internal entry the photoset the route
  // names is the authority. "Missing" means it is not among the property's
  // sets; "assetLoadError" means the photosets fetch itself failed. Kept apart
  // so a failed request never reads as "this order has no photoset". Both stay
  // false on the account entry.
  const [routePhotosetMissing, setRoutePhotosetMissing] = useState(false);
  const [assetLoadError, setAssetLoadError] = useState(false);
  // DV-2861: the photosets read succeeded and returned no set the caller may
  // see. asset-service drops client-hidden sets from a customer's list, so
  // for a customer this is "every set here is hidden"; a failed read stays
  // false (it is not a reason to claim the set is unavailable).
  const [noVisiblePhotoset, setNoVisiblePhotoset] = useState(false);
  // Bumped by retryAssetLoad to re-run the load effect. retryPropertyFetch
  // cannot do it: it re-sets propertyMeta with equal primitives, so none of
  // the effect's dependencies change.
  const [assetLoadAttempt, setAssetLoadAttempt] = useState(0);
  // One log per (property, photoset), so a retry or a StrictMode double effect
  // cannot inflate the post-release rate.
  const loggedMissingRef = useRef<string | null>(null);
  // Tracks the photoset whose images are currently loaded, so the load effect
  // can ignore a photosetUUID change that just re-targets the already-loaded
  // set — e.g. the auto-redirect below that canonicalizes the URL. Without
  // this, that self-inflicted URL change reloads the same photoset (a flash).
  const loadedPhotosetRef = useRef<string | null>(null);

  // DV-2751: "the services this user can order" — asset pages are the asset-only
  // role's hottest path, so skip the request entirely for roles the cart route
  // rejects rather than fetch and discard. With the query skipped, data stays
  // undefined and the memo below empties naturally, so every consumer (action-bar
  // service pills, the viewer's BadgeCarousel) drops its ordering affordances
  // without its own gate.
  // DV-2849: the internal entry never offers ordering — an editor arriving
  // from the internal console is there to caption, hide and reorder, not to
  // buy services on the client's behalf. Same skip-the-request mechanism
  // DV-2751 uses for asset-only roles.
  const canOrderServices = !isInternalEntry && canCreateOnRoute('Order');
  const { data: assetServicesData } = useGetAssetServicesQuery(undefined, {
    skip: !canOrderServices,
  });
  // DV-3586: an inactive service is never offered, whatever products-api
  // returns; an unpriced one cannot be quoted.
  const assetServiceList = useMemo<ServiceType[]>(
    () =>
      (assetServicesData ?? []).filter(
        (s) => isActiveService(s) && s.defaultPricing !== null
      ),
    [assetServicesData]
  );

  useEffect(() => {
    document.title = 'PlanOmatic - Assets';
    return () => {
      document.title = 'PlanOmatic';
    };
  }, []);

  const fetchPropertyMeta = useCallback(() => {
    if (!propertyId) return;
    setIsLoadError(false);
    setIsNotFound(false);
    getPropertyByIdTrigger(propertyId)
      .unwrap()
      .then((res) => {
        const p = res.content?.[0];
        if (p) {
          // Same display rule as the properties list (getPropertyAddress:
          // address2/unit placement, the Buildium single-family unit
          // omission, dedup cleanup) — the router-state seed above IS that
          // rule's output, so the header never switches text when this
          // fetch replaces the seed (QA round 5, spec §1.1).
          const { streetLine, localityLine } = toStreetAndLocality(
            getPropertyAddress(p)
          );
          setPropertyMeta({
            propertyAddress: localityLine
              ? `${streetLine}, ${localityLine}`
              : streetLine,
            streetLine,
            localityLine,
            propertyType: p.propertyType,
            hasPhotoset: p.hasPhotoset ?? false,
            propertySource: p.propertySource ?? '',
            orderUUID: p.orderUUID || null,
            orderStatus: p.orderStatus ?? null,
            address1: p.address1,
            unitNumber: p.unitNumber ?? null,
            // Blank reads as none: an empty eyebrow is worse than no eyebrow.
            entity: p.entity || null,
            entityUUID: p.entityUUID ?? null
          });
        } else {
          // Empty content array — property is not in the current org's scope.
          // Matches usePropertyDetail's contract for cross-org deep links.
          setIsNotFound(true);
        }
      })
      .catch((err: unknown) => {
        // 4xx (invalid UUID, 404) = "no such property" — retry won't help,
        // surface as not-found. 5xx / network errors are reserved for the
        // load-error hero so the user gets a real Try-again affordance.
        if (is4xxError(err)) {
          setIsNotFound(true);
        } else {
          setIsLoadError(true);
        }
      });
  }, [propertyId, getPropertyByIdTrigger]);

  useEffect(() => {
    if (propertyId) fetchPropertyMeta();
    // fetchPropertyMeta is propertyId-stable (useCallback) — this runs once
    // per property. Always fetch, even when router state seeded propertyMeta
    // for the header paint: that state lacks the raw address1/unitNumber
    // fields the Download Assets zip filename needs.
  }, [propertyId, fetchPropertyMeta]);

  // Primitives-only deps for the load effect below, not the propertyMeta
  // OBJECT: fetchPropertyMeta always re-fetches (even when router state
  // already seeded propertyMeta for the header paint, to pick up the raw
  // address1/unitNumber fields) and calls setPropertyMeta with a NEW object
  // once it resolves. If the effect depended on the propertyMeta object
  // itself, that re-set — even with equal hasPhotoset/orderUUID values —
  // would re-run the effect and duplicate the vendor/photoset/order fetches
  // (spinner flash, setImageAssets re-dispatch resetting selections). These
  // are the only propertyMeta fields the effect body reads.
  const hasPropertyMeta = propertyMeta !== null;
  const metaHasPhotoset = propertyMeta?.hasPhotoset ?? false;
  // DV-3521: the order this workspace is about: the one it was opened for,
  // or the property's latest only when the entry named none. The no-photoset
  // grid shows its photos, and it is the upload tag's default (see
  // workspaceOrderUUID below), so an upload never lands outside the grid it
  // was made from.
  const openedOrderUUID = contextOrderUUID ?? propertyMeta?.orderUUID ?? null;
  // A photoset in the internal route overrides `property.hasPhotoset`: that
  // flag can lag (it is raised by whoever creates a set), and when it reads
  // false the order-only branch below would show the property's LATEST
  // order's photos, whatever the route names.
  const routeNamesPhotoset = isInternalEntry && Boolean(photosetUUID);

  useEffect(() => {
    if (!hasPropertyMeta || !propertyId) return;
    // Skip when the requested photoset is already the loaded one: the only
    // way photosetUUID changes to an already-loaded value is the redirect
    // below canonicalizing the URL. Reloading it would blank and refetch the
    // identical photoset. A genuine switch (different id) falls through.
    if (photosetUUID && photosetUUID === loadedPhotosetRef.current) {
      // A superseded run (below) leaves `loading` to its successor; when the
      // successor is this skip — browser Back onto the loaded set mid-switch —
      // nothing else would clear it.
      setLoading(false);
      return;
    }
    // Set by the cleanup when the route moves on (or the page unmounts) before
    // this run's requests resolve. A superseded run applies nothing: its late
    // response would put another photoset's photos, or its error, under the
    // current URL. Checked after every await.
    let superseded = false;
    const load = async () => {
      setLoading(true);
      setRoutePhotosetMissing(false); // PR 3
      setAssetLoadError(false); // PR 3
      setNoVisiblePhotoset(false); // DV-2861
      try {
        if (metaHasPhotoset || routeNamesPhotoset) {
          let res = await getPhotosetsTrigger(propertyId).unwrap();
          if (superseded) return;
          // DV-3399: right after Create Photoset the route names a set this
          // list may not have yet. The create's tag invalidation refetches
          // this entry through RTK's middleware, which RTK 1.9.7 does not
          // track as a running query, so a trigger issued while it is in
          // flight resolves at once with the pre-create cache. Wait for that
          // refetch to land, then read once more.
          if (
            photosetUUID &&
            !res.some((x) => x.photoset.photosetId === photosetUUID)
          ) {
            await photosetsListSettled(
              () => store.getState(),
              propertyId,
              () => superseded
            );
            if (superseded) return;
            res = await getPhotosetsTrigger(propertyId).unwrap();
            if (superseded) return;
          }
          const sorted = res
            .map((x) => ({
              photosetId: x.photoset.photosetId,
              name: x.photoset.name,
              createdAt: x.photoset.createdAt,
              lastWebhookTriggeredAt: x.photoset.lastWebhookTriggeredAt ?? null,
              lastPmsSyncedAt: x.photoset.lastPmsSyncedAt ?? null,
              assets: x.assets.filter(isPhotoAsset),
              zillowUrls: x.assets
                .filter((a) => a.type === 'url')
                .map((a) => ({ url: a.url ?? '' })),
              assetUUIDs:
                x.photoset.assetUUIDs ?? x.assets.map((a) => a.assetUUID),
              hasUnsyncedChanges: x.photoset.hasUnsyncedChanges ?? false,
              hasUnsyncedPmsChanges: x.photoset.hasUnsyncedPmsChanges ?? false,
              // Passed through un-coerced (no `?? false`): mapAssets needs to
              // tell an old backend's absent field from an explicit false.
              orderCustomized: x.photoset.orderCustomized,
              // DV-2861: raw for the same reason — null must stay null.
              visibleToClient: x.photoset.visibleToClient,
              publicToken: x.photoset.publicToken ?? null,
              orderUUID: x.photoset.orderUUID ?? null,
              origin: x.photoset.origin ?? null
            }))
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            );
          setPhotosets(sorted);
          setNoVisiblePhotoset(sorted.length === 0);
          // PR 3: the route can now bring us here with the property flag false.
          setHasPhotoset(metaHasPhotoset || sorted.length > 0);
          const target = photosetUUID
            ? sorted.find((ps) => ps.photosetId === photosetUUID)
            : sorted[0];
          // PR 3 (spec §4.5 item 3): never a neighboring set on the internal
          // entry. The page renders PhotosetNotFound instead.
          // Nothing is shown after this, so the already-loaded skip must not
          // match the set shown before, and its id (which gates Upload) and
          // photos must not stay under the page that replaces them.
          const clearLoadedPhotoset = () => {
            loadedPhotosetRef.current = null;
            setSelectedPhotosetId(null);
            setZillow3d(null);
            dispatch(resetImageAssets());
          };
          if (routeNamesPhotoset && photosetUUID && !target) {
            clearLoadedPhotoset();
            setRoutePhotosetMissing(true);
            const missKey = `${propertyId}/${photosetUUID}`;
            if (loggedMissingRef.current !== missKey) {
              loggedMissingRef.current = missKey;
              // Joined into one string so it reads as one property in
              // LogRocket. Empty: the property returned no photosets at all.
              // Populated: a dangling order link, or a set on another property.
              track('photoset-not-found', {
                propertyUUID: propertyId,
                photosetUUID,
                loadedPhotosetIds: sorted.map((ps) => ps.photosetId).join(',')
              });
            }
            return;
          }
          const ps = target || sorted[0];
          if (ps) {
            loadedPhotosetRef.current = ps.photosetId;
            setSelectedPhotosetId(ps.photosetId);
            dispatch(
              setImageAssets(
                mapAssets(ps.assets, ps.photosetId, ps.orderCustomized)
              )
            );
            setZillow3d(
              ps.zillowUrls.length > 0
                ? ps.zillowUrls[ps.zillowUrls.length - 1].url
                : null
            );
          } else {
            // DV-2861: an empty list. A switch keeps the page mounted, so
            // without this the set shown before stays selected behind the
            // customer's unavailable copy.
            clearLoadedPhotoset();
          }
        } else if (openedOrderUUID) {
          const res = await getAssetsByOrderTrigger({
            orderUUID: openedOrderUUID,
            type: AssetManagement.PHOTO
          }).unwrap();
          if (superseded) return;
          const photos = res.filter(isPhotoAsset);
          if (photos.length > 0) {
            dispatch(setImageAssets(mapAssets(photos)));
            const synced = photos.filter(
              (i) => i.isSyncedNonWatermarked || i.isSyncedWatermarked
            );
            if (synced.length > 0) {
              const latest = synced.reduce((a, b) =>
                new Date(a.updatedAt ?? 0) > new Date(b.updatedAt ?? 0) ? a : b
              );
              // Kept raw and formatted at render: an unparseable value then reads
              // "Invalid Date" as before, instead of throwing here (toISOString
              // does) and failing the whole load. No updatedAt keeps today's
              // "now" fallback.
              setOrderLastSyncedAt(latest.updatedAt ?? new Date().toISOString());
            }
          }
          const urls = res.filter((i) => i.type === 'url');
          if (urls.length > 0) setZillow3d(urls[urls.length - 1].url);
        }
      } catch {
        if (superseded) return;
        // PR 3: the account entry keeps its silent empty grid (banked, spec
        // §6). The internal entry must not — a failed fetch there would sit
        // beside the missing state and read as "this order has no photoset".
        if (isInternalEntry) {
          // Nothing is loaded now, so the already-loaded skip must not match
          // the set shown before this attempt: browser Back onto it has to
          // re-run the load, not keep this error under its URL.
          loadedPhotosetRef.current = null;
          setAssetLoadError(true);
        }
      } finally {
        if (!superseded) setLoading(false);
      }
    };
    load();
    return () => {
      superseded = true;
    };
  }, [
    propertyId,
    hasPropertyMeta,
    metaHasPhotoset,
    openedOrderUUID,
    photosetUUID,
    routeNamesPhotoset,
    assetLoadAttempt,
    isInternalEntry,
    dispatch,
    store,
    getPhotosetsTrigger,
    getAssetsByOrderTrigger
  ]);

  // PR 3: Try Again on the photos load-error hero. Clears the loaded ref so
  // the effect's already-loaded skip cannot swallow the re-run.
  const retryAssetLoad = useCallback(() => {
    loadedPhotosetRef.current = null;
    setAssetLoadAttempt((n) => n + 1);
  }, []);

  // Clear cached assets only when leaving the page — not on every load re-run
  // (a photoset switch), which would blank the grid before the new set loads.
  useEffect(
    () => () => {
      dispatch(resetImageAssets());
    },
    [dispatch]
  );

  // DV-2849: every in-page photoset navigation must stay inside the entry's
  // own route family. `/home` is `entity: 'required'`, so sending an unscoped
  // internal session there bounces it off the route guard to `/` and
  // force-switches it into an account it never chose — which would break the
  // no-photoset variant of the internal route that spec §3.1 requires. Both
  // call sites below carry the current search params themselves, so `view` is
  // deliberately NOT passed to the builder: it would be emitted twice.
  const photosetPath = useCallback(
    (propertyUUID: string, photosetUUID: string) =>
      isInternalEntry
        ? buildRoute.globalOrderAssets(propertyUUID, { photosetUUID })
        : buildRoute.propertyAssets(propertyUUID, { photosetUUID }),
    [isInternalEntry]
  );

  useEffect(() => {
    if (
      propertyMeta?.hasPhotoset &&
      photosets.length > 0 &&
      !photosetUUID &&
      propertyId
    ) {
      navigate(
        {
          pathname: photosetPath(propertyId, photosets[0].photosetId),
          search: searchParams.toString()
        },
        { replace: true }
      );
    }
    // photosetPath is stable per mount (it closes over the entry only); listed
    // so the redirect can never keep building a stale route family.
  }, [propertyMeta, photosets, photosetUUID, propertyId, photosetPath]);

  useEffect(() => {
    setSelectedImages(imageAssets.filter((img) => img.isChecked));
  }, [imageAssets]);

  const photoSetOptions = useMemo(
    () => photosets.map((ps) => ({ value: ps.photosetId, label: ps.name })),
    [photosets]
  );

  const selectedPhotoset = useMemo(
    () => photosets.find((ps) => ps.photosetId === selectedPhotosetId) ?? null,
    [photosets, selectedPhotosetId]
  );

  // DV-3521: the open photoset is one Connect created for a different order
  // than the one this workspace was opened for, and the opened order is not
  // itself linked to it (an add-on sharing the shoot's set: deliberate). This
  // is how 947831's photos went into another order's photoset. A
  // Legacy-ingested set is exempt: its `orderUUID` is back-filled from one
  // arbitrary photo and names no creating order. Both halves mirror
  // asset-service's PhotosetWriteOrder, which files an upload into a
  // Connect-created set under that set's order and leaves a Legacy set's
  // uploads with the caller's order.
  const photosetIsAnotherOrders =
    Boolean(contextOrderUUID) &&
    selectedPhotoset !== null &&
    selectedPhotoset.origin === PHOTOSET_ORIGIN_CONNECT &&
    Boolean(selectedPhotoset.orderUUID) &&
    selectedPhotoset.orderUUID !== contextOrderUUID &&
    selectedPhotoset.photosetId !== contextOrderPhotosetUUID;
  // DV-3521: the order an upload made here is tagged with. The one the
  // workspace was opened for (the property's latest when none was), except
  // into another order's Connect-created set, whose order asset-service would
  // file the photo under anyway; sending it keeps the tag and the set in step
  // on the customer entry too, where there is no warning to read.
  const workspaceOrderUUID = photosetIsAnotherOrders
    ? selectedPhotoset.orderUUID
    : openedOrderUUID;
  // DV-3521: the stage of the order an upload is tagged with, for the upload
  // gate, so it judges the same order the tag names. That is
  // the opened order, or the set's order when the open set is another order's
  // (opening delivered X onto in-flight Y's set must not enable an upload
  // filed as Y). propertyMeta.orderStatus is the property's LATEST order's
  // stage and is used only when the entry named no order. The internal entry
  // has no account chosen, so its order read is unscoped, as asset-service's
  // own internal read of an order is.
  const { currentData: workspaceOrder } = useGetOrderByUUIDQuery(
    { orderUUID: workspaceOrderUUID ?? '', unscoped: isInternalEntry },
    { skip: !contextOrderUUID || !workspaceOrderUUID }
  );
  // Null until the order read lands, which the upload gate reads as not
  // delivered, so it starts closed and opens once the real value lands, never
  // the reverse (same rule as the router-state seed above). `currentData`, not
  // `data`: when the tagged order changes, `data` would still hold the
  // previous order's stage until the new read lands.
  const workspaceOrderStatus = contextOrderUUID
    ? (workspaceOrder?.orderStatus ?? null)
    : (propertyMeta?.orderStatus ?? null);
  // DV-3714: the selected photoset's OWN order, for the share gate. Before
  // minting a public link asset-service judges `photoset.orderUUID`
  // (PublicGalleryServiceImpl.requireDelivered), which is often not the
  // workspace's order: an entry that names no order (every properties-side
  // entry) falls back to the property's latest order, a reshoot in progress
  // while the delivered set from the shoot before is on screen; and an add-on
  // opened onto its shoot's set judges the add-on. RTK shares the request
  // when it is the workspace's order. Not read where Share is not offered
  // (prod, while the flag is off): nothing else needs this order.
  const photosetOrderUUID = selectedPhotoset?.orderUUID ?? null;
  const { currentData: photosetOrder, isError: photosetOrderFailed } =
    useGetOrderByUUIDQuery(
      { orderUUID: photosetOrderUUID ?? '', unscoped: isInternalEntry },
      { skip: !shareOffered || !photosetOrderUUID }
    );
  const photosetOrderStage = toPhotosetOrderStage({
    orderUUID: photosetOrderUUID,
    orderRead: photosetOrder,
    orderReadFailed: photosetOrderFailed
  });

  // Photoset path: derive the "last synced" header line from the persisted
  // photoset stamp (survives refresh, reflects other users' syncs). The
  // orderLastSyncedAt STATE remains only for the legacy order-only path (no
  // photoset), which has nothing to stamp and keeps its updatedAt
  // approximation.
  const pmsLastSyncedLine =
    selectedPhotoset?.lastPmsSyncedAt && pmsSource
      ? ` Photos last synced to ${pmsSource} on ${dayjs(
          selectedPhotoset.lastPmsSyncedAt
        ).format('LLLL')}`
      : '';
  const orderLastSyncedLine =
    orderLastSyncedAt && pmsSource
      ? ` Photos last synced to ${pmsSource} on ${dayjs(orderLastSyncedAt).format('LLLL')}`
      : '';

  const handlePhotoSetChange = useCallback(
    async (value: string) => {
      if (!propertyId) return;
      const params: Record<string, string> = {};
      searchParams.forEach((v, k) => {
        params[k] = v;
      });
      navigateTo(photosetPath(propertyId, value), params);
    },
    [propertyId, searchParams, navigateTo, photosetPath]
  );

  // Optimistic badge clear after a successful Sync webhook push —
  // mirrors the backend clearing BOTH channel flags on trigger-webhook (the
  // webhook delivers everything: captions, reorders, hides, uploads).
  const markPhotosetSynced = useCallback((photosetId: string) => {
    setPhotosets((prev) =>
      prev.map((ps) =>
        ps.photosetId === photosetId
          ? { ...ps, hasUnsyncedChanges: false, hasUnsyncedPmsChanges: false }
          : ps
      )
    );
  }, []);

  // DV-2524 inverse of markPhotosetSynced: a trash or recover raises BOTH
  // channel flags server-side (removing/re-adding a photo is a deliverable
  // change on the webhook AND the PMS channel, same contract as hide). Our copy
  // of the photoset is fetched once at page load, so mirror the raise locally
  // or the unsynced badge stays dark until the next full load.
  const markPhotosetUnsynced = useCallback((photosetId: string) => {
    setPhotosets((prev) =>
      prev.map((ps) =>
        ps.photosetId === photosetId
          ? { ...ps, hasUnsyncedChanges: true, hasUnsyncedPmsChanges: true }
          : ps
      )
    );
  }, []);

  // Optimistic mirror of the backend mark-pms-synced stamp after a successful
  // direct PMS sync: stamps lastPmsSyncedAt and clears hasUnsyncedPmsChanges
  // without waiting for a refetch. The PMS-channel flag is unmixed by
  // construction (it only ever tracks PMS-deliverable edits — uploads,
  // hides/unhides), so a successful PMS sync clearing it is safe. It does
  // NOT clear hasUnsyncedChanges: caption edits and reordering never reach
  // any PMS, only the webhook, so that flag stays owed until a successful
  // webhook push (see markPhotosetSynced). The backend write (guarded by a
  // pre-upload snapshot) is the source of truth on next load/refetch either
  // way — this is just immediate UI feedback.
  const markPhotosetPmsSynced = useCallback((photosetId: string) => {
    const syncedAt = new Date().toISOString();
    setPhotosets((prev) =>
      prev.map((ps) =>
        ps.photosetId === photosetId
          ? {
              ...ps,
              lastPmsSyncedAt: syncedAt,
              hasUnsyncedPmsChanges: false
            }
          : ps
      )
    );
  }, []);

  // DV-2861: adopt the visibility the server returned for one photoset. This
  // field only: the PATCH answers with the whole photoset, but its order list
  // and unsynced flags can be older or newer than ours (a reorder in flight,
  // local edits awaiting a sync), so merging it would clobber them.
  const setPhotosetVisibility = useCallback(
    (photosetId: string, visibleToClient: boolean) => {
      setPhotosets((prev) =>
        prev.map((ps) =>
          ps.photosetId === photosetId ? { ...ps, visibleToClient } : ps
        )
      );
    },
    []
  );

  // Public PhotoPlan gallery: adopt the share token the server minted (or
  // null after a revoke) for one photoset. Same field-only merge as
  // setPhotosetVisibility, for the same reason.
  const setPhotosetPublicToken = useCallback(
    (photosetId: string, publicToken: string | null) => {
      setPhotosets((prev) =>
        prev.map((ps) =>
          ps.photosetId === photosetId ? { ...ps, publicToken } : ps
        )
      );
    },
    []
  );

  // Success handler for the Sync PMS sync. Photoset path mirrors the
  // backend stamp + PMS-channel flag clear; the legacy order-only path
  // (nothing to stamp) keeps the session-local header line so the user still
  // sees immediate feedback. clearPmsDirtyFlags fires on BOTH paths — a
  // PMS-deliverable edit (e.g. a hide) can exist on an order-only page with
  // no photoset in play, and that dirtyPms mirror is just as real there; it
  // must clear on a successful sync regardless of whether there's a
  // photoset to stamp. This mirrors the webhook side's unconditional clear
  // in markPhotosetSynced.
  //
  // syncedPhotosetId lets the caller (the Sync modal) pass back the
  // exact photoset id it synced, taken at the moment the sync request was
  // made — preferred over the live selectedPhotosetId, which could have
  // moved on by the time this callback fires. Falls back to
  // selectedPhotosetId for callers that don't have a more precise id.
  const handlePmsSyncSuccess = useCallback(
    (syncedPhotosetId?: string) => {
      dispatch(clearPmsDirtyFlags());
      const targetPhotosetId = syncedPhotosetId ?? selectedPhotosetId;
      if (targetPhotosetId) {
        markPhotosetPmsSynced(targetPhotosetId);
        return;
      }
      setOrderLastSyncedAt(new Date().toISOString());
    },
    [selectedPhotosetId, markPhotosetPmsSynced, dispatch]
  );

  // Scope downloads to exactly what the grid shows. The grid is scoped to a single photoset/order,
  // but propertyUUID resolves to EVERY photo on the property across all orders — so on a property
  // with multiple orders, "select all" of the 23 displayed photos was downloading the property's
  // full 38 (incl. photos from other orders the user never saw). Use photoUUIDs instead: photoUUID
  // is populated on every photo asset (the old reason for preferring propertyUUID — orderUUID being
  // null on legacy assets — doesn't apply to photoUUIDs), and it matches the grid exactly. (DV-2162)
  // The any-checked decision runs over the FULL pool, hidden included, and a
  // checked hidden photo stays in the selection. Hidden is only excluded from
  // the nothing-checked bulk default (product AC, DV-2221) — that same rule is
  // enforced by the backend (downloadZip drops hidden photos from a
  // multi-photo request unless includeHidden is sent, and always allows a
  // single explicit photoUUID), so the client dropping checked hidden photos
  // was redundant — and doing it BEFORE the any-checked decision made a
  // hidden-only selection degenerate to "Download All" (DV-2820 review).
  const selectionFromPool = (pool: ImageAssetType[]): AssetSelection => {
    const checked = pool.filter((img) => img.isChecked);
    // "Download All": nothing is checked — the bulk default excludes
    // hidden; the download modal's include-hidden opt-in is the way in.
    if (checked.length === 0) return downloadAllSelection(pool);
    // Something is checked — scope to exactly what's checked.
    return { photoUUIDs: checked.map((img) => img.photoUUID) };
  };

  // Staged photos are real committed Assets (DV-2252) and sync/download like
  // any other photo.
  // DV-2856: "Download All" means all DISPLAYED — the same pool Select All and
  // the count label use — so the button can't quietly disagree with the grid
  // the user is looking at.
  // The include-hidden opt-in is drawn from the SAME displayed pool
  // (DV-2763 review): a search matching no visible photo must not offer, or
  // send, hidden photos the search filtered out. Every PHOTOS-tab download
  // modal opens with this. The Virtual Staging tab's pools are deliberately
  // not it: they are staged-scoped (internal-only), a different population
  // (see StagedLeadingActions / StagedMenuItems on the page). The Floor Plan
  // tab's are (DV-3735), narrowed to its pool.
  const getDownloadScope = (): DownloadScope => {
    const displayed = imageAssets.filter((img) =>
      isInQueryScope(img, { searchQuery, pool: photoPool })
    );
    return {
      selection: selectionFromPool(displayed),
      hiddenPhotoUUIDs: hiddenDownloadPool(
        displayed,
        isInternalAssetTeamUser()
      )
    };
  };

  // DV-2750: sync is always whole-photoset — checkbox selection and the
  // hidden filter apply to downloads only. The full grid list (hidden
  // included) goes to the backend: property-service filters hidden photos
  // from upload itself, and must SEE the hidden ones to run the
  // hidden-synced PMS delete sweep (DV-2221 Buildium/AppFolio, DV-2552
  // Rentvine). Explicit photoUUIDs (not propertyUUID) keeps the sync
  // scoped to the photoset the user is viewing (DV-2162).
  // A view filter (hidden, DV-2856 search) must never reach this: the backend
  // needs the whole set, not the slice of it currently on screen.
  const getSyncSelection = (): AssetSelection => ({
    photoUUIDs: imageAssets.map((img) => img.photoUUID)
  });

  // Virtual Staging tab download: same 0-or-all semantics, staged pool only.
  const getStagedDownloadSelection = (): AssetSelection =>
    selectionFromPool(
      imageAssets.filter(
        (img) => img.staged && matchesAssetQuery(img, searchQuery)
      )
    );

  // DV-714: the unit-photos endpoint now supports non-Single-Family AppFolio
  // properties, so the propertyType gate on AppFolio sync is gone. Sync
  // surfaces whenever the property is Connect-sync'd to a PMS.
  const showSyncButton = canShowPmsSync({
    pmsSource,
    propertySource: propertyMeta?.propertySource
  });

  const backAction = () => {
    if (isInternalEntry) {
      // Carry only the tab. Search and filters live in sessionStorage
      // (`pmc:filters:global-orders`) and survive the round trip on their own.
      const view = searchParams.get('view');
      navigateTo(ROUTES.GLOBAL_ORDERS, view ? { view } : {});
      return;
    }
    const target = accountBackTarget(source, searchParams.get('view'));
    navigateTo(target.route, target.query);
  };

  const backTooltip = isInternalEntry
    ? 'Back to Global Orders'
    : accountBackTarget(source, searchParams.get('view')).tooltip;

  // Org name for the not-found state; see usePropertyDetail (BC-9).
  const orgItem = getOrgSelectedAllData()[0];
  const currentOrgName = orgItem?.name ?? 'this organization';

  return {
    propertyMeta,
    contextOrderUUID,
    contextOrderPhotosetUUID,
    workspaceOrderUUID,
    workspaceOrderStatus,
    photosetOrderStage,
    photosetIsAnotherOrders,
    isNotFound,
    isLoadError,
    retryPropertyFetch: fetchPropertyMeta,
    routePhotosetMissing,
    assetLoadError,
    retryAssetLoad,
    noVisiblePhotoset,
    currentOrgName,
    loading,
    zillow3d,
    lastSynced: pmsLastSyncedLine || orderLastSyncedLine,
    handlePmsSyncSuccess,
    pmsSource,
    pmsAccount,
    isPaused,
    pmsStatusPending,
    hasPhotoset,
    photoSetOptions,
    selectedPhotosetId,
    selectedPhotoset,
    handlePhotoSetChange,
    markPhotosetSynced,
    markPhotosetUnsynced,
    setPhotosetVisibility,
    setPhotosetPublicToken,
    imageAssets,
    selectedImages,
    assetServiceList,
    showSyncButton,
    getDownloadScope,
    getSyncSelection,
    getStagedDownloadSelection,
    backAction,
    backTooltip,
    propertyUUID: propertyId ?? ''
  };
}
